import React, { useEffect, useRef, useState } from "react";
import HorseComparison from "./components/HorseComparison";
import PaddockHistoryCard from "./components/PaddockHistoryCard";
import usePaddockHistorySummary from "./components/usePaddockHistorySummary";
import AiPrediction from "./components/AiPrediction";
import { Authenticator, View, Button, Text, Loader } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { generateClient, get } from "aws-amplify/api";
import { getCurrentUser, fetchUserAttributes } from "aws-amplify/auth";
import { updatePaddockDiagnosis, createPaddockDiagnosis } from "./graphql/mutations";

const client = generateClient();


// --- 枠色設定（そのまま） ---
const WAKU_COLORS = {
  1: { bg: "#ffffff", text: "#000000", border: "#cccccc" },
  2: { bg: "#333333", text: "#ffffff", border: "#333333" },
  3: { bg: "#ff9999", text: "#000000", border: "#ff4d4d" },
  4: { bg: "#6699ff", text: "#000000", border: "#3366ff" },
  5: { bg: "#ffffaa", text: "#000000", border: "#cccc00" },
  6: { bg: "#80c080", text: "#000000", border: "#008000" },
  7: { bg: "#ffd280", text: "#000000", border: "#ffa500" },
  8: { bg: "#ffb6c1", text: "#000000", border: "#ff69b4" },
};

// --- 競馬場コード→日本語名 ---
const JRA_PLACES = {
  "01": "札幌",
  "02": "函館",
  "03": "福島",
  "04": "新潟",
  "05": "東京",
  "06": "中山",
  "07": "中京",
  "08": "京都",
  "09": "阪神",
  "10": "小倉",
};

// --- 評価タイプごとの色 ---
export const EVAL_COLORS = {
  positive: { bg: "#E8F5E9", text: "#2E7D32" },   // 緑
  negative: { bg: "#FFEBEE", text: "#C62828" },   // 赤
  neutral:  { bg: "#FFF3E0", text: "#EF6C00" },   // オレンジ
};

// --- 各項目と評価 ---
export const EVALUATION_MAP = {
  "気合乗OK": "positive",
  "落着きOK": "positive",
  "チャカ": "negative",
  "ｲﾚ込み": "negative",
  "薄": "negative",
  "太め": "negative",
  "細め": "negative",
  "踏込良": "positive",
  "軽やか": "positive",
  "固め": "negative",
  "コズミ": "negative",
  "ハリ": "positive",
  "外歩き": "positive",
  "発汗": "negative",
  "ゆるい": "negative",
  "二人引": "neutral",
  "最後尾": "neutral",
  "返し馬○": "positive",
  "返し馬×": "negative",
};

// --- 単勝オッズ ---
// SQLで倍率へ変換済みなので、ここでは10で割らない。
const formatWinOdds = (value) => {
  const text = String(value ?? "").trim();

  if (!/^\d+(?:\.\d+)?$/.test(text)) return "—";

  const odds = Number(text);

  return Number.isFinite(odds) && odds > 0
    ? `${odds.toFixed(1)}倍`
    : "—";
};

// --- 人気 ---
const formatPopularity = (value) => {
  const text = String(value ?? "").trim();

  if (!/^\d+$/.test(text)) return "—";

  const rank = Number(text);

  return Number.isSafeInteger(rank) && rank > 0
    ? `${rank}人気`
    : "—";
};

// --- yyyy/mm/ddを日付へ変換 ---
// UTCを使い、端末のタイムゾーンによる日付のずれを防ぐ。
const parseRaceDate = (value) => {
  const text = String(value ?? "").trim();
  const match = /^(\d{4})\/(\d{2})\/(\d{2})$/.exec(text);

  if (!match) return null;

  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);

  if (y < 1000 || m < 1 || m > 12 || d < 1 || d > 31) {
    return null;
  }

  const result = new Date(Date.UTC(y, m - 1, d));

  return result.getUTCFullYear() === y &&
    result.getUTCMonth() === m - 1 &&
    result.getUTCDate() === d
    ? result
    : null;
};

// --- 出走間隔 ---
// 月曜始まりの週で計算。
// 翌週は連闘、2週後は中1週、3週後は中2週。
const formatRaceInterval = (previousDate, raceYear, raceMonthDay) => {
  const previous = parseRaceDate(previousDate);
  const yearText = String(raceYear ?? "").trim();
  const monthDay = String(raceMonthDay ?? "").trim();

  if (!/^\d{4}$/.test(yearText) || !/^\d{4}$/.test(monthDay)) {
    return "—";
  }

  const current = parseRaceDate(
    `${yearText}/${monthDay.slice(0, 2)}/${monthDay.slice(2)}`
  );

  if (!previous || !current || previous >= current) {
    return "—";
  }

  const dayMs = 24 * 60 * 60 * 1000;

  const monday = (date) =>
    date.getTime() - ((date.getUTCDay() + 6) % 7) * dayMs;

  const weeks = Math.round(
    (monday(current) - monday(previous)) / (7 * dayMs)
  );

  if (weeks === 0) return "同週出走";
  if (weeks === 1) return "連闘";

  return `中${weeks - 1}週`;
};

export default function App() {
  // --- nickname取得 ---
  const [nickname, setNickname] = useState("");

  const loadNickname = async () => {
    try {
      const attrs = await fetchUserAttributes();
      setNickname(attrs.nickname || "(未設定)");
    } catch (error) {
      console.error("属性取得エラー:", error);
    }
  };

  useEffect(() => {
    loadNickname();
  }, []);

  const [user, setUser] = useState(null);
  const [year, setYear] = useState("");
  const [date, setDate] = useState("");
  const [place, setPlace] = useState("");
  const [raceNo, setRaceNo] = useState("");
  const [horses, setHorses] = useState([]);
  const [activeRaceTab, setActiveRaceTab] = useState("paddock");
  const [raceInfo, setRaceInfo] = useState({});
  const [diagnosis, setDiagnosis] = useState({});
  const [loading, setLoading] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableRaces, setAvailableRaces] = useState({});
  const [availablePlaces, setAvailablePlaces] = useState({});
  const [recommendedHorses, setRecommendedHorses] = useState([]); // 🟢 推奨馬
  const [communityRecommended, setCommunityRecommended] = useState([]); // 🟢 みんなの推奨馬
  // 🧩 自身の評価完了状態を管理
  const [isMyEvaluationDone, setIsMyEvaluationDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const raceLoadSequence = useRef(0);
  const raceOperation = useRef(null);
  const diagnosisOperation = useRef(null);
  const selectedRaceId = year && date && place && raceNo ? `${year}${date}${place}${raceNo.padStart(2, "0")}` : "";
  const historySummary = usePaddockHistorySummary(selectedRaceId, !loading && horses.length > 0 && activeRaceTab === "paddock");

  // --- 選択情報をlocalStorageから復元 ---
  useEffect(() => {
    const savedYear = localStorage.getItem("year");
    const savedDate = localStorage.getItem("date");
    const savedPlace = localStorage.getItem("place");
    const savedRaceNo = localStorage.getItem("raceNo");

    if (savedYear) setYear(savedYear);
    if (savedDate) setDate(savedDate);
    if (savedPlace) setPlace(savedPlace);
    if (savedRaceNo) setRaceNo(savedRaceNo);
  }, []);

  // --- 選択情報を保存 ---
  useEffect(() => {
    if (year) localStorage.setItem("year", year);
  }, [year]);

  useEffect(() => {
    if (date) localStorage.setItem("date", date);
  }, [date]);

  useEffect(() => {
    if (place) localStorage.setItem("place", place);
  }, [place]);

  useEffect(() => {
    if (raceNo) localStorage.setItem("raceNo", raceNo);
  }, [raceNo]);

  // --- 復元後に日付プルダウンを再構築 ---
  useEffect(() => {
    if (year && !availableDates.length) {
      fetchDates(year);
    }
  }, [year]);

  // --- 評価ロジック ---
  const evaluateHorse = (d) => {
    let score = 0;

    if (d.attitude === "気合乗OK") score += 2;
    if (d.attitude === "落着きOK") score += 1;
    if (d.attitude === "チャカ") score -= 1;
    if (d.attitude === "薄") score -= 1;
    if (d.attitude === "ｲﾚ込み") score -= 2;

    if (d.body === "太め") score -= 1;
    if (d.body === "細め") score -= 1;

    if (d.walk === "踏込良") score += 2;
    if (d.walk === "軽やか") score += 2;
    if (d.walk === "固め") score -= 2;
    if (d.walk === "コズミ") score -= 1;

    if (d.hari) score += 2;
    if (d.gaiboki) score += 1;
    if (d.hakkan) score -= 2;
    if (d.yurui) score -= 1;
    if (d.kaesiOK) score += 2;
    if (d.kaesiNG) score -= 2;

    let rank = "B";

    if (score >= 4) rank = "A";
    else if (score <= 0) rank = "C";

    return { ...d, score, rank };
  };

  // --- 推奨馬抽出 ---
  const getRecommendedHorses = (horses) => {
    const evaluated = horses.map((h) => evaluateHorse(h));

    // A評価の馬のみ抽出
    const aRanked = evaluated.filter((h) => h.rank === "A");

    // 3頭を超える場合はスコア順に上位3頭のみを採用
    const recommended =
      aRanked.length > 3
        ? aRanked.sort((a, b) => b.score - a.score).slice(0, 3)
        : aRanked;

    return recommended;
  };

  // --- ユーザー認証情報取得 ---
  useEffect(() => {
    (async () => {
      const current = await getCurrentUser();
      console.log("current.userId is ", current.userId);
      setUser(current);
    })();
  }, []);

  // --- レースデータ取得 ---
  async function fetchRaceData(sequence) {
    if (!year || !date || !place || !raceNo) return;

    console.log("🔄 レースデータ取得開始");
    setLoading(true);

    try {
      const race_id = `${year}${date}${place}${raceNo.padStart(2, "0")}`;
      const file = `${race_id}.csv`;

      const operation = get({
        apiName: "readRaceCsvdev",
        path: "/getRaceData",
        options: {
          queryParams: { year, date, file },
        },
      });
      raceOperation.current = operation;
      const { body } = await operation.response;
      const data = await body.json();
      if (sequence !== raceLoadSequence.current) return;

      const sorted = (data.horse_list || []).sort(
        (a, b) => Number(a.Umaban) - Number(b.Umaban)
      );

      setHorses(sorted);
      setRaceInfo(data.race_info || {});

      // 🧩 レースデータ取得完了後に診断情報を続けて取得（順序保証）
      if (user) {
        console.log("🏇 診断データ取得開始（レースデータ完了後）");
        await fetchDiagnosis(sorted, sequence);
      }
    } catch (err) {
      if (sequence !== raceLoadSequence.current) return;
      console.error("Error fetching race data:", err);
      alert("出走馬データの取得に失敗しました");
    } finally {
      if (sequence === raceLoadSequence.current) setLoading(false);
      console.log("✅ レースデータ取得完了");
    }
  }

  // --- 日付一覧取得 ---
  const fetchDates = async (selectedYear) => {
    try {
      const { body } = await get({
        apiName: "readRaceCsvdev",
        path: "/listRaceFiles",
        options: {
          queryParams: { year: selectedYear },
        },
      }).response;

      const data = await body.json();

      const uniqueDates = [...new Set(data.map((item) => item.date))].sort(
        (a, b) => Number(b) - Number(a)
      );

      setAvailableDates(uniqueDates);

      const racesByDate = {};

      data.forEach((item) => {
        if (!racesByDate[item.date]) {
          racesByDate[item.date] = [];
        }
        racesByDate[item.date].push(item.raceNo);

        // 🔽 重複を削除（Setを使って簡単に）
        racesByDate[item.date] = [...new Set(racesByDate[item.date])];
      });

      // console.log(racesByDate);

      setAvailableRaces(racesByDate);

      // --- 競馬場リスト生成 ---
      const racesByPlace = {};

      data.forEach((item) => {
        const date = item.date;
        const placeCode = item.place;
        const placeName = JRA_PLACES[placeCode] || placeCode;

        if (!Array.isArray(racesByPlace[date])) {
          racesByPlace[date] = [];
        }

        racesByPlace[date].push({
          code: placeCode,
          name: placeName,
        });
      });

      // 🔹 重複削除（codeでユニーク化）
      Object.keys(racesByPlace).forEach((date) => {
        racesByPlace[date] = Array.from(
          new Map(racesByPlace[date].map((p) => [p.code, p])).values()
        );
      });

      // console.log(racesByPlace);

      setAvailablePlaces(racesByPlace);
    } catch (error) {
      console.error("listRaceFiles呼び出し失敗:", error);
    }
  };

  // --- 年変更 ---
  const handleYearChange = async (event) => {
    const selectedYear = event.target.value;

    setYear(selectedYear);
    setDate("");
    setPlace("");
    setRaceNo("");
    setAvailableDates([]);
    setAvailableRaces({});

    if (selectedYear) {
      await fetchDates(selectedYear);
    }
  };

  // 出走表・自身の診断は1回だけ取得。切替前の応答を適用しない。
  useEffect(() => {
    const sequence = ++raceLoadSequence.current;
    if (year && date && place && raceNo && user) {
      fetchRaceData(sequence);
    } else {
      setHorses([]); setRaceInfo({}); setDiagnosis({}); setLoading(false);
    }
    return () => {
      // Invalidate a request sequence, not a DOM ref.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (raceLoadSequence.current === sequence) raceLoadSequence.current++;
      raceOperation.current?.cancel();
      diagnosisOperation.current?.cancel();
    };
    // fetchRaceData uses the selected values; including it would reload on every edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, date, place, raceNo, user]);

  // --- レース切り替え時に評価をリセット ---
  useEffect(() => {
    setRecommendedHorses([]);
    setCommunityRecommended([]);
    setIsMyEvaluationDone(false);
    setDiagnosis({});
  }, [year, date, place, raceNo]);

  // --- 入力変更 ---
  const handleChange = (umaId, field, value) => {
    setDiagnosis((prev) => ({
      ...prev,
      [umaId]: {
        ...prev[umaId],
        [field]: value,
      },
    }));
  };

  // --- 診断情報取得 ---
  const fetchDiagnosis = async (horsesArg, sequence = raceLoadSequence.current) => {
    const targetHorses = horsesArg || horses;

    if (!user || targetHorses.length === 0) return;

    const race_id = `${year}${date}${place}${raceNo.padStart(2, "0")}`;

    // console.log("race_id is ", race_id);
    // console.log("username is ", user.username);

    try {
      const operation = get({
        apiName: "readRaceCsvdev",
        path: "/getPaddockDiagnosisByRace",
        options: {
          queryParams: {
            race_id,
            user_id: user.username,
          },
        },
      });
      diagnosisOperation.current = operation;
      const { body } = await operation.response;
      const data = await body.json();
      if (sequence !== raceLoadSequence.current) return;
      console.log("data is ", data);

      if (data.length !== 0) {
        console.log("既に出走馬情報アリ：みんなの推奨馬情報取得へ");
        setIsMyEvaluationDone(true);
      }

      const merged = targetHorses.map((h) => {
        const match = data.find((d) => d.uma_id == h.Umaban);

        return match
          ? {
              ...h,
              diagnosis: match,
            }
          : {
              ...h,
              diagnosis: {
                body: "",
                attitude: "",
                walk: "",
                comment: "",
                hari: false,
                gaiboki: false,
                hakkan: false,
                yurui: false,
                saigo: false,
              },
            };
      });

      // console.log("マージ後 horses:", merged);
      setHorses(merged);

      // --- diagnosisステート再構築 ---
      const mapped = {};

      merged.forEach((h) => {
        mapped[h.Umaban] = h.diagnosis;
      });

      setDiagnosis(mapped);

      // --- 推奨馬反映 ---
      const recs = data
        .filter((d) => d.isRecommended)
        .map((d) => d.uma_id);

      setRecommendedHorses(recs);
    } catch (err) {
      console.error("診断データ取得エラー:", err);
    }
  };

  // --- みんなの推奨馬取得 ---
  useEffect(() => {
    if (isMyEvaluationDone) {
      console.log("みんなの推奨馬取得 取得開始");

      const race_id = `${year}${date}${place}${raceNo.padStart(2, "0")}`;
      fetchCommunityRecommended(race_id);
    }
  }, [isMyEvaluationDone]);

  async function fetchCommunityRecommended(race_id) {
    try {
      console.log(
        "isMyEvaluationDone-1 in fetchCommunityRecommended is",
        isMyEvaluationDone
      );

      console.log("みんなの推奨馬取得処理 開始");

      const { body } = await get({
        apiName: "readRaceCsvdev",
        path: "/getCommunityRecommended",
        options: {
          queryParams: {
            race_id,
          },
        },
      }).response;

      const data = await body.json();

      console.log("return of getCommunityRecommended is", data.recommended);

      setCommunityRecommended(data.recommended);

      console.log(
        "isMyEvaluationDone-2 in fetchCommunityRecommended is",
        isMyEvaluationDone
      );
    } catch (err) {
      console.error("みんなの推奨馬取得エラー:", err);
    }
  }

  useEffect(() => {
    if (!isMyEvaluationDone) return;

    const race_id = `${year}${date}${place}${raceNo.padStart(2, "0")}`;
    fetchCommunityRecommended(race_id);
  }, [isMyEvaluationDone]);

  // --- 全頭保存 ---
  const handleSaveAll = async () => {
    if (!user) return;

    const race_id = `${year}${date}${place}${raceNo.padStart(2, "0")}`;
    const entries = Object.entries(diagnosis);

    try {
      setSaving(true);

      const evaluatedList = [];

      // 全頭評価
      for (const [uma_id, values] of entries) {
        const { score, rank } = evaluateHorse(values);

        evaluatedList.push({
          race_id,
          uma_id,
          user_id: user.username,
          ...values,
          evaluation: rank,
          score,
        });
      }

      // A評価上位3頭
      const recommended = getRecommendedHorses(evaluatedList);
      const recommendedIds = recommended.map((r) => r.uma_id);

      // 登録・更新
      for (const data of evaluatedList) {
        data.isRecommended = recommendedIds.includes(data.uma_id);

        const mutation = data.id
          ? updatePaddockDiagnosis
          : createPaddockDiagnosis;

        const {
          __typename,
          createdAt,
          updatedAt,
          ...cleanData
        } = data;

        const response = await client.graphql({
          query: mutation,
          variables: {
            input: cleanData,
          },
        });

        const savedItem =
          response.data?.createPaddockDiagnosis ||
          response.data?.updatePaddockDiagnosis;

        if (savedItem?.id) {
          data.id = savedItem.id;
        }
      }

      setRecommendedHorses(recommendedIds);

      await fetchDiagnosis();

      showAutoCloseAlert(
        `全頭の診断を保存しました！ 推奨馬: ${
          recommendedIds.join(", ") || "なし"
        }`
      );
    } catch (err) {
      console.error("全保存エラー:", err);
    } finally {
      setSaving(false);
    }
  };

  // --- 自動で閉じる通知 ---
  function showAutoCloseAlert(message, duration = 3000) {
    const existing = document.getElementById("auto-alert");

    if (existing) {
      existing.remove();
    }

    const alertDiv = document.createElement("div");
    alertDiv.id = "auto-alert";
    alertDiv.textContent = message;

    Object.assign(alertDiv.style, {
      position: "fixed",
      top: "20px",
      right: "20px",
      background: "#333",
      color: "#fff",
      padding: "10px 20px",
      borderRadius: "8px",
      zIndex: 9999,
      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
      opacity: 0,
      transition: "opacity 0.3s",
    });

    document.body.appendChild(alertDiv);

    requestAnimationFrame(() => {
      alertDiv.style.opacity = 1;
    });

    setTimeout(() => {
      alertDiv.style.opacity = 0;
      setTimeout(() => alertDiv.remove(), 300);
    }, duration);
  }

  return (
    <Authenticator signUpAttributes={["email", "nickname"]}>
      {({ signOut, user }) => {
        const handleSignOut = async () => {
          localStorage.removeItem("year");
          localStorage.removeItem("date");
          localStorage.removeItem("place");
          localStorage.removeItem("raceNo");

          await signOut();
        };

        return (
          <View padding="0.5rem" maxWidth="720px" margin="0 auto">
            {loading && (
              <div className="overlay">
                <Loader variation="linear" size="large" />
                <p style={{ color: "#fff", marginTop: "12px" }}>
                  レースデータ読込中...
                </p>
              </div>
            )}

            {saving && (
              <div className="overlay">
                <Loader variation="linear" size="large" />
                <p style={{ color: "#fff", marginTop: "12px" }}>
                  保存中です...
                </p>
              </div>
            )}

            <h1
              style={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                color: "#1E1E1E",
              }}
            >
              みんなのパドック
              <span
                style={{
                  fontSize: "0.75em",
                  color: "#0A84FF",
                  marginLeft: "6px",
                }}
              >
                (β)
              </span>
            </h1>

            <Text style={{ color: "#555", fontSize: "0.9rem" }}>
              ログイン中: {nickname}
            </Text>

            {/* レース選択 */}
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
                flexWrap: "wrap",
                margin: "0.8rem 0",
              }}
            >
              <select
                value={year}
                onChange={handleYearChange}
                className="modern-select"
              >
                <option value="">年</option>
                <option value="2026">2026</option>
                <option value="2025">2025</option>
              </select>

              <select
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="modern-select"
              >
                <option value="">日付</option>
                {availableDates.map((d) => (
                  <option key={d} value={d}>
                    {`${d.slice(0, 2)}月${d.slice(2)}日`}
                  </option>
                ))}
              </select>

              <select
                value={place}
                onChange={(e) => setPlace(e.target.value)}
                className="modern-select"
              >
                <option value="">場所</option>
                {(availablePlaces[date] || []).map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.name}
                  </option>
                ))}
              </select>

              <select
                value={raceNo}
                onChange={(e) => setRaceNo(e.target.value)}
                className="modern-select"
              >
                <option value="">R</option>
                {(availableRaces[date] || []).map((r) => (
                  <option key={r} value={r}>
                    {r}R
                  </option>
                ))}
              </select>
            </div>

            {/* レース情報 */}
            {raceInfo && raceInfo.HassoTime && (
              <div
                style={{
                  position: "sticky",
                  top: 0,
                  background: "#f9f9f9",
                  padding: "0.4rem",
                  zIndex: 1000,
                }}
              >
                <Text as="h4">
                  【 {JRA_PLACES[raceInfo.JyoCD]} {raceInfo.RaceNum}R】
                  {" "}
                  {raceInfo.Hondai ?? ""}{" "}
                  {raceInfo.ｸﾞﾚｰﾄﾞ == "" ? "" : raceInfo.ｸﾞﾚｰﾄﾞ}
                  {" "}
                  {raceInfo.種別} {raceInfo.クラス}
                  {" "}
                  {raceInfo.出走頭数}頭
                </Text>

                <Text>
                  {`${raceInfo.芝ダ}${raceInfo.Kyori}m ${
                    raceInfo.HassoTime.slice(0, 2)
                  }:${raceInfo.HassoTime.slice(2)} 発走
                  （${raceInfo.天候}:${
                    raceInfo.芝ダ === "芝"
                      ? raceInfo.馬場状態芝
                      : raceInfo.馬場状態ダ
                  }）`}
                </Text>

                {recommendedHorses.length > 0 && (
                  <div
                    style={{
                      marginTop: "0.3rem",
                      background: "#FFF3CD",
                      border: "1px solid #0044CC",
                      borderRadius: "6px",
                      padding: "0.3rem 0.5rem",
                    }}
                  >
                    <strong>推奨馬:</strong>
                    {" "}
                    {recommendedHorses.join(", ")}
                  </div>
                )}

                {communityRecommended?.length > 0 && (
                  <div
                    style={{
                      marginTop: "0.5rem",
                      background: "#e8f4ff",
                      border: "1px solid #0044CC",
                      padding: "0.5rem",
                      borderRadius: "6px",
                    }}
                  >
                    <strong>みんなの推奨馬:</strong>
                    {" "}
                    {communityRecommended?.join(", ") || "データ集計中..."}
                  </div>
                )}
              </div>
            )}

            <div className="race-view-tabs" role="tablist" aria-label="レース画面">
              <button type="button" role="tab" id="paddock-tab" aria-selected={activeRaceTab === "paddock"} aria-controls="paddock-panel" onClick={() => setActiveRaceTab("paddock")}>パドック診断</button>
              <button type="button" role="tab" id="comparison-tab" aria-selected={activeRaceTab === "comparison"} aria-controls="comparison-panel" onClick={() => setActiveRaceTab("comparison")}>出走馬比較</button>
              <button type="button" role="tab" id="ai-tab" aria-selected={activeRaceTab === "ai"} aria-controls="ai-panel" onClick={() => setActiveRaceTab("ai")}>AI予想</button>
            </div>
            <div role="tabpanel" id="comparison-panel" aria-labelledby="comparison-tab" hidden={activeRaceTab !== "comparison"}>
              {activeRaceTab === "comparison" && (year && date && place && raceNo ? <HorseComparison raceId={`${year}${date}${place}${raceNo.padStart(2, "0")}`} wakuColors={WAKU_COLORS} /> : <p>年・日付・競馬場・レース番号を選択してください。</p>)}
            </div>
            <div role="tabpanel" id="ai-panel" aria-labelledby="ai-tab" hidden={activeRaceTab !== "ai"}>
              {activeRaceTab === "ai" && (year && date && place && raceNo ? <AiPrediction raceId={`${year}${date}${place}${raceNo.padStart(2, "0")}`} /> : <p>年・日付・競馬場・レース番号を選択してください。</p>)}
            </div>
            <div role="tabpanel" id="paddock-panel" aria-labelledby="paddock-tab" hidden={activeRaceTab !== "paddock"}>
            {/* 出走馬カード */}
            {horses.map((h, i) => {
              const wakuNum = Number(h.Wakuban);
              const color = WAKU_COLORS[wakuNum] || {};
              const d = diagnosis[h.Umaban] || {};
              const isRec = recommendedHorses.includes(h.Umaban);

              return (
                <div
                  key={i}
                  className="horse-card"
                  style={{
                    border: `2px solid ${
                      isRec ? "#0044CC" : color.border
                    }`,
                    background: color.bg,
                    color: color.text,
                    boxShadow: isRec ? "0 0 10px #0044CC" : "none",
                  }}
                >
                  <Text
                    style={{
                      fontWeight: "bold",
                      fontSize: "1rem",
                      color: "inherit",
                    }}
                  >
                    {h.Umaban}：{h.Bamei} {h.SexCD}{Number(h.才)}{" "}
                    {h.馬体重 || "---"}kg
                    {" "}
                    ({h.前走馬体重との差 || "-"})
                  </Text>

                  {/* 追加：オッズ・人気・出走間隔 */}
                  <Text
                    style={{
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      color: "inherit",
                      marginTop: "0.2rem",
                    }}
                  >
                    単勝 {formatWinOdds(h.Odds)}
                    {" ／ "}
                    {formatPopularity(h.Ninki)}
                    {" ／ 間隔 "}
                    {formatRaceInterval(
                      h["前走出走日"],
                      h.Year ?? raceInfo.Year,
                      h.MonthDay ?? raceInfo.MonthDay
                    )}
                  </Text>

                  <Text style={{ fontSize: "0.9rem", color: "inherit" }}>
                    騎手: {h.騎手名}({Number(h.斤量)})
                    {" "}
                    {h.調教師名}:{h.馬主名}
                  </Text>

                  <Text style={{ color: "inherit" }}>
                    1週前:{h["1週前追切コース種別"]}
                    {" "}
                    {h["1週前追切 坂路4F時計 / ウッド6F時計"]}
                    {" "}
                    最終:{h["最終追切コース種別"]}
                    {" "}
                    {h["最終追切 坂路4F時計 / ウッド6F時計"]}
                  </Text>

                  <PaddockHistoryCard
                    key={`${selectedRaceId}-${h.Umaban}`}
                    raceId={selectedRaceId}
                    horseNo={h.Umaban}
                    horseName={h.Bamei}
                    kettoNum={h.KettoNum}
                    state={historySummary}
                  />

                  {/* 気配・体型・歩様 */}
                  <div
                    style={{
                      display: "flex",
                      gap: "0.4rem",
                      flexWrap: "wrap",
                      marginTop: "0.6rem",
                    }}
                  >
                    {["attitude", "body", "walk"].map((field, idx) => {
                      const selectedValue = d[field] || "";
                      const evalKey = EVALUATION_MAP[selectedValue];
                      const evalColor = evalKey ? EVAL_COLORS[evalKey] : {};

                      return (
                        <select
                          key={idx}
                          value={selectedValue}
                          onChange={(e) =>
                            handleChange(h.Umaban, field, e.target.value)
                          }
                          className="modern-select small"
                          style={{
                            backgroundColor: evalColor.bg || "#f8f9fb",
                            color: evalColor.text || "#333",
                            borderColor: evalColor.border || "#ccc",
                            fontWeight: selectedValue ? 600 : 400,
                          }}
                        >
                          {field === "attitude" && (
                            <>
                              <option value="">(気配)</option>
                              <option>気合乗OK</option>
                              <option>落着きOK</option>
                              <option>チャカ</option>
                              <option>ｲﾚ込み</option>
                              <option>薄</option>
                            </>
                          )}

                          {field === "body" && (
                            <>
                              <option value="">(体型)</option>
                              <option>太め</option>
                              <option>細め</option>
                            </>
                          )}

                          {field === "walk" && (
                            <>
                              <option value="">(歩様)</option>
                              <option>踏込良</option>
                              <option>軽やか</option>
                              <option>固め</option>
                              <option>コズミ</option>
                            </>
                          )}
                        </select>
                      );
                    })}
                  </div>

                  {/* チェック項目 */}
                  <div
                    style={{
                      marginTop: "0.6rem",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "0.4rem",
                    }}
                  >
                    {[
                      { label: "ハリ", field: "hari" },
                      { label: "外歩き", field: "gaiboki" },
                      { label: "発汗", field: "hakkan" },
                      { label: "ゆるい", field: "yurui" },
                      { label: "二人引", field: "twoman" },
                      { label: "最後尾", field: "saigo" },
                      { label: "返し馬○", field: "kaesiOK" },
                      { label: "返し馬×", field: "kaesiNG" },
                    ].map(({ label, field }) => {
                      const active = d[field];
                      const evalKey = EVALUATION_MAP[label];
                      const evalColor = evalKey ? EVAL_COLORS[evalKey] : {};

                      return (
                        <button
                          key={field}
                          onClick={() =>
                            handleChange(h.Umaban, field, !active)
                          }
                          type="button"
                          style={{
                            padding: "0.3rem 0.8rem",
                            borderRadius: "18px",
                            fontSize: "0.85rem",
                            cursor: "pointer",
                            border: `1px solid ${
                              active ? evalColor.border : "#ccc"
                            }`,
                            backgroundColor: active ? evalColor.bg : "#fff",
                            color: active ? evalColor.text : "#444",
                            fontWeight: active ? 600 : 400,
                            transition: "0.2s",
                          }}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>

                  <textarea
                    value={d.comment || ""}
                    onChange={(e) =>
                      handleChange(h.Umaban, "comment", e.target.value)
                    }
                    placeholder="コメントを入力"
                    rows={2}
                    className="modern-textarea"
                  />
                </div>
              );
            })}

            {horses.length > 0 && (
              <div
                style={{
                  marginTop: "1.2rem",
                  textAlign: "center",
                }}
              >
                <Button onClick={handleSaveAll} variation="primary">
                  全頭保存
                </Button>

                <Button variation="link" onClick={handleSignOut}>
                  サインアウト
                </Button>

                <footer className="footer">
                  © 2025 みんなのパドック
                </footer>
              </div>
            )}

            </div>
            <style>{`
              .horse-card {
                border-radius: 12px;
                padding: 1rem;
                margin-top: 0.8rem;
                box-shadow: 0 2px 6px rgba(0,0,0,0.08);
                transition: transform 0.15s ease, box-shadow 0.15s ease;
              }

              .horse-card:hover {
                transform: translateY(-2px);
                box-shadow: 0 4px 10px rgba(0,0,0,0.12);
              }

              .modern-select {
                border: 1px solid #ccc;
                border-radius: 8px;
                padding: 0.4rem 0.8rem;
                font-size: 0.9rem;
                background: #f8f9fb;
                transition: 0.2s;
              }

              .modern-select:focus {
                outline: none;
                border-color: #4a6cf7;
                background: #eef2ff;
              }

              .modern-select.small {
                min-width: 100px;
              }

              .toggle-btn {
                border: 1px solid #ccc;
                border-radius: 20px;
                padding: 0.3rem 0.8rem;
                font-size: 0.85rem;
                background: #fff;
                color: #444;
                cursor: pointer;
                transition: all 0.2s ease;
              }

              .toggle-btn.active {
                background: #4a6cf7;
                color: #fff;
                border-color: #4a6cf7;
              }

              .modern-textarea {
                width: 100%;
                border-radius: 8px;
                border: 1px solid #ddd;
                margin-top: 0.5rem;
                padding: 0.6rem;
                font-size: 0.9rem;
                transition: border 0.2s ease;
              }

              .modern-textarea:focus {
                outline: none;
                border-color: #4a6cf7;
                background: #fff;
              }

              .footer {
                position: static;
                bottom: 0;
                width: 100%;
                background-color: #fff;
                text-align: center;
                font-size: 12px;
                padding: 8px 0;
                color: #666;
                border-top: 1px solid #ddd;
              }

              .overlay {
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: rgba(0,0,0,0.4);
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                z-index: 9999;
              }
            `}</style>
          </View>
        );
      }}
    </Authenticator>
  );
}


